import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Address } from '../../../../api/app-types/address.app';
import { ContactPoint, ContactPointCategory } from '../../../../api/app-types/contact-point.app';
import { CreateContactPoint } from '../../../../api/app-types/relative.app';
import { RoleId } from '../../../../api/app-types/role.app';
import { User } from '../../../../api/app-types/user.app';
import { UpdateWorkplaceBody } from '../../../../api/app-types/workplace.app';
import { WORKPLACE_SERVICE } from '../../../../api/services/workplace.api';
import { AddressCompletion } from '../../../../core/components/address-completion/address-completion';
import { BranchField } from '../../../../core/components/branch-field/branch-field';
import { LvrContactPoint } from '../../../../core/components/contact-point/contact-point';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { isContactPointComplete } from '../../../../shared/helpers/is-contact-point-complete.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { RouteInjection } from '../../../../shared/routing/route-injection.class';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { InstitutionAndWorkplace } from '../../../../shared/routing/resolvers/workplace.resolver';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';

@Component({
	selector: 'lvr-institution-workplace-edit',
	imports: [
		LvrHeading,
		TranslocoPipe,
		ReactiveFormsModule,
		LvrAutoSaveDirective,
		LvrField,
		LvrAppendAsteriskPipe,
		MatFormField,
		MatInputModule,
		LvrInputDirective,
		MatSelectModule,
		AddressCompletion,
		MatIcon,
		MatIconButton,
		LvrPaddingDirective,
		BranchField,
		LvrContactPoint,
		LvrRestoreActions,
		LvrDefaultActions,
		LvrButton,
	],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
	],
	templateUrl: './institution-workplace-edit.html',
	styleUrl: './institution-workplace-edit.scss',
})
export class InstitutionWorkplaceEdit extends RouteInjection {
	private readonly env = inject(ENVIRONMENT);
	private readonly branchRegex = new RegExp(this.env.branchNameRegexStr);
	private readonly draftService = inject(FormPersistenceService);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly api = inject(WORKPLACE_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly transloco = inject(TranslocoService);
	private readonly data = toSignal(this.route.data, {
		requireSync: true,
	});
	private readonly institutionAndWorkplace = computed(
		() => this.data()['institutionAndWorkplace'] as InstitutionAndWorkplace,
	);
	private readonly institution = computed(() => this.institutionAndWorkplace().institution);
	private readonly workplace = computed(() => this.institutionAndWorkplace().workplace);
	public readonly maxRemarkLength = this.env.textareaMaxLength;

	public readonly hasError = hasError;
	public readonly eaaRole = RoleId.EAA;
	public readonly saving = signal(false);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly draftId = computed(() => FORM_KEYS.UPDATE_INSTITUTION_WORKPLACE + '.' + this.workplace().id);
	public readonly user = computed(() => this.data()!['user'] as User);
	private readonly contactPointCategoriesData =
		this.resolveDataAsSignal<PaginatedResponse<ContactPointCategory>>('contactPointCategories');
	public readonly contactPointCategories = computed<ContactPointCategory[]>(
		() => this.contactPointCategoriesData()?.entities ?? [],
	);
	public readonly canEditInstitutionsPermission = hasPermission(this.session.permissions, 'Institutions.Edit');

	public readonly form = this.initForm(inject(FormBuilder));

	public get contactPoints(): ContactPoint[] {
		return (this.form.controls.contactPoints?.value ?? []) as ContactPoint[];
	}

	public addContactPoint() {
		if (this.contactPoints.length >= 5) return;
		const ctrl = this.form.controls.contactPoints;
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const cp: ContactPoint = {
			id: -1,
			preferredWayOfContact: false,
			categoryContactPointId: -1,
			contactValue: '',
			remark: '',
			personId: -1,
			personTypeId: -1,
		};
		values.push(cp);
		ctrl?.patchValue(values);
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.controls.contactPoints;
		if (!ctrl) return;

		const values = (ctrl.value ?? []) as ContactPoint[];

		if (index >= 0 && index < values.length) {
			Object.assign(values[index], data.value); // Update same item in array
		} else {
			values.push(data.value); // Add if new
		}
	}

	public removeContactPoint(index: number) {
		const ctrl = this.form.controls.contactPoints;
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		if (index !== -1) {
			values.splice(index, 1);
			ctrl?.patchValue(values);
		}
	}

	/**
	 * Cancels update workplace, deletes draft and navigates to workplace detail view.
	 */
	public async cancel(): Promise<void> {
		this.draftService.deleteDraft('institution-workplace', this.draftId());
		await this.router.navigate(['../'], { relativeTo: this.route });
	}

	/**
	 * Handles workplace update, deletes draft and navigates to workplace detail view.
	 */
	public async update() {
		if (this.saving()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const EMPTY_ADDRESS = {
			streetAndHouseNumber: '',
			postalCodeAndCity: '',
			soundexStreet: '',
			anonym: '',
			identifier: '',
		};

		this.saving.set(true);

		try {
			const fv = this.form.value;
			const addressRemark = fv.addressRemark?.trim() ?? '';
			const resolvedAddress =
				fv.address === null || fv.address === undefined
					? ({ ...EMPTY_ADDRESS, identifier: addressRemark } as Address)
					: ({ ...fv.address, identifier: addressRemark } as Address);

			const body: UpdateWorkplaceBody = {
				name: fv.name?.trim() ?? '',
				address: resolvedAddress,
				industryName: fv.industryName ?? '',
				contactPoints: [],
			};

			const filteredCps = (fv.contactPoints ?? []).filter(isContactPointComplete);

			if (filteredCps.length > 0) {
				const additionalCps: CreateContactPoint[] = filteredCps.map((cp) => ({
					id: cp.id === -1 ? undefined : cp.id,
					preferredWayOfContact: cp.preferredWayOfContact,
					categoryContactPointId: cp.categoryContactPointId,
					contactValue: cp.contactValue?.trim(),
					remark: cp.remark?.trim(),
				}));

				body.contactPoints!.push(...additionalCps);
			}

			await this.api.update(this.institution().id, this.workplace().id!, body);
			await this.cancel();
			this.ntfySrv.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.workplace_updated')),
			);
		} catch {
			this.ntfySrv.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.workplace_update_error')),
			);
		} finally {
			this.saving.set(false);
		}
	}

	private initForm(fb: FormBuilder) {
		return fb.group({
			name: fb.control(this.workplace().name, [Validators.required, Validators.pattern(this.branchRegex)]),
			addressRemark: fb.control(this.workplace().address?.identifier ?? '', [
				Validators.maxLength(this.maxRemarkLength),
			]),
			address: fb.control<Address | undefined>(this.workplace().address),
			contactPoints: fb.control<ContactPoint[]>(this.workplace().contactPoints ?? [], {
				validators: [Validators.maxLength(5)],
			}),
			industryName: fb.control<string>(this.workplace().industryName),
		});
	}
}
